import { useState, useEffect } from "react";
import {
  Box,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { isFulfilled } from "@reduxjs/toolkit";

import useMessage from "../../../hooks/useMessage";
import { useAppDispatch } from "../../../app/hooks";
import { ExpenseTypesGrid } from "./ExpenseTypesGrid";
import { ExpenseType, ExpenseTypeMapping } from "../settingsApi";
import {
  fetchExpenseTypeMappings,
  updateExpenseTypes,
  fetchExpenseTypeMappingsForDate,
} from "../settingsActions";
import { ValidFromDateModal } from "../helpers/ValidFromDateModal";
import { CreateExpenseTypeMapping } from "./CreateExpenseTypeMapping";
import { reduceExpenseTypes } from "../helpers/utils";
import { RoundedButton } from "../../../common/components/styled/styled";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";

export const ExpenseTypesControlPanel = () => {
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();
  const { t } = useTranslation();

  const [expenseTypeMappings, setExpenseTypeMappings] = useState<string[]>([]);
  const [selectedExpenseTypeMapping, setSelectedExpenseTypeMapping] =
    useState<string>("");
  const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNewSet, setIsNewSet] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const selectExpenseTypeDate = (event: SelectChangeEvent) => {
    setSelectedExpenseTypeMapping(event.target.value as string);
  };

  const setupNewConfiguration = (selectedDate: Date) => {
    setIsNewSet(true);
    const dateString =
      selectedDate.getFullYear() +
      "-" +
      ("0" + (selectedDate.getMonth() + 1)).slice(-2) +
      "-" +
      ("0" + selectedDate.getDate()).slice(-2);
    setSelectedExpenseTypeMapping(dateString);
    setOpenModal(false);
  };

  const cancelNewConfiguration = () => {
    setIsLoading(true);
    setIsNewSet(false);
    setOpenModal(false);
    setSelectedDate(null);
    setSelectedExpenseTypeMapping("");
    setExpenseTypes([]);
    dispatch(fetchExpenseTypeMappings({})).then((action) => {
      if (isFulfilled(action)) {
        setExpenseTypeMappings(action.payload);
        sendMessage(t("FETCH_SUCCESS"), "success");
      }
      setIsLoading(false);
    });
  };

  const undoLocalChanges = () => {
    setIsLoading(true);
    dispatch(
      fetchExpenseTypeMappingsForDate({ date: selectedExpenseTypeMapping }),
    ).then((action) => {
      if (isFulfilled(action)) {
        setExpenseTypes(action.payload.expenseTypes);
        sendMessage(t("FETCH_SUCCESS"), "success");
      }
      setIsLoading(false);
    });
  };

  const updateExpenseTypeMappings = (expenseTypes: ExpenseType[]) => {
    setIsLoading(true);
    const array = reduceExpenseTypes(expenseTypes);
    const newExpenseTypes: ExpenseTypeMapping = {
      validFrom: selectedExpenseTypeMapping,
      expenseTypes: array,
    };
    dispatch(updateExpenseTypes(newExpenseTypes)).then((action) => {
      if (isFulfilled(action)) {
        setExpenseTypes(action.payload.expenseTypes);
        sendMessage(t("SAVING_SUCCESS"), "success");
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchExpenseTypeMappings({})).then((action) => {
      if (isFulfilled(action)) {
        setExpenseTypeMappings(action.payload);
      }
      setIsLoading(false);
    });
  }, [dispatch]);

  useEffect(() => {
    if (selectedExpenseTypeMapping !== "") {
      setIsLoading(true);
      dispatch(
        fetchExpenseTypeMappingsForDate({ date: selectedExpenseTypeMapping }),
      ).then((action) => {
        if (isFulfilled(action)) {
          setExpenseTypes(action.payload.expenseTypes);
          sendMessage(t("FETCH_SUCCESS"), "success");
        }
        setIsLoading(false);
      });
    }
    // Adding sendMessage to dependencies causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExpenseTypeMapping, dispatch, t]);

  return (
    <Box>
      {isNewSet ? (
        <CreateExpenseTypeMapping
          selectedExpenseTypeMapping={selectedExpenseTypeMapping}
          closeView={cancelNewConfiguration}
        />
      ) : (
        <>
          {/* TODO refactor into its own component */}
          <Box display="flex" flexDirection="column" width="200px">
            <RoundedButton
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ marginBottom: 3 }}
              onClick={() => setOpenModal(true)}
            >
              {t("addNewExpenseType", { ns: "settings" })}
            </RoundedButton>
            <FormControl>
              <InputLabel>{t("effectiveFrom", { ns: "settings" })}</InputLabel>
              <Select
                value={selectedExpenseTypeMapping}
                label={t("effectiveFrom", { ns: "settings" })}
                onChange={selectExpenseTypeDate}
              >
                {expenseTypeMappings.map((expenseTypeMapping) => (
                  <MenuItem key={expenseTypeMapping} value={expenseTypeMapping}>
                    {expenseTypeMapping}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <ExpenseTypesGrid
            expenseTypes={expenseTypes}
            onSave={updateExpenseTypeMappings}
            onUndo={undoLocalChanges}
          />
          <ValidFromDateModal
            isOpen={openModal}
            onAccept={setupNewConfiguration}
            onCancel={cancelNewConfiguration}
            selectedDate={selectedDate}
            label={t("settings:addNewExpenseType")}
          />
          <OverlaySpinner isLoading={isLoading} />
        </>
      )}
    </Box>
  );
};
